// Dev → requires .env
// Production → requires runtime injection
// No fallback to baked values
// Immediate crash if misconfigured

type RuntimeConfig = {
  FHIR_URL?: string;
  MSW_URL?: string;
  MSW_TERMINOLOGY_URL?: string;
  TERMINOLOGY_URL?: string;
  KEYCLOAK_URL?: string;
  KEYCLOAK_REALM?: string;
  KEYCLOAK_CLIENT?: string;
  MOD_TIME_THRESHOLD?: number;
  CLINICALTRIALS_SYSTEM_URL?: string;
  MOLIT_ID_SYSTEM_URL?: string;
  DRKS_SYSTEM_URL?: string;
  TRIALS_BACKEND_URL?: string;
  SUPPORT_MAIL?: string;
  OVERPASS_URL?: string;
  CHANGED_WILDTYPE_GENE_SYSTEM?: string;
  MATOMO_ENABLED?: boolean;
  MATOMO_URL?: string;
  MATOMO_SITE_ID?: string;
  CATS_DOCS_PATIENTS?: string;
  CATS_DOCS?: string;
  SENTRY_ACTIVE?: boolean;
  SENTRY_DSN?: string;
  SENTRY_REPLAY_ACTIVE?: boolean;
  SENTRY_TRACING_ACTIVE?: boolean;
  SENTRY_ENVIRONMENT?: string;
};

// Takes into account which variables are guaranteed to be populated (because the app intentionally fails if not)
type CatsConfig = {
  FHIR_URL: string;
  MSW_URL?: string;
  MSW_TERMINOLOGY_URL?: string;
  TERMINOLOGY_URL: string;
  KEYCLOAK_URL: string;
  KEYCLOAK_REALM: string;
  KEYCLOAK_CLIENT: string;
  MOD_TIME_THRESHOLD: number;
  CLINICALTRIALS_SYSTEM_URL: string;
  MOLIT_ID_SYSTEM_URL: string;
  DRKS_SYSTEM_URL: string;
  TRIALS_BACKEND_URL: string;
  SUPPORT_MAIL: string;
  OVERPASS_URL: string;
  CHANGED_WILDTYPE_GENE_SYSTEM: string;
  MATOMO_ENABLED: boolean;
  MATOMO_URL?: string;
  MATOMO_SITE_ID?: string;
  CATS_DOCS_PATIENTS: string;
  CATS_DOCS: string;
  SENTRY_ACTIVE: boolean;
  SENTRY_DSN?: string;
  SENTRY_REPLAY_ACTIVE?: boolean;
  SENTRY_TRACING_ACTIVE?: boolean;
  SENTRY_ENVIRONMENT?: string;
};

// List of environment variables that are not required in all cases; can be for testing etc.
const optionalEnvVars = [
  'MSW_URL',
  'MSW_TERMINOLOGY_URL',
  'MATOMO_URL',
  'MATOMO_SITE_ID',
  'SENTRY_DSN',
  'SENTRY_REPLAY_ACTIVE',
  'SENTRY_TRACING_ACTIVE',
  'SENTRY_ENVIRONMENT',
];

const VITE_PREFIX = 'VITE_';

/** Checks if a variable is optional, regardless of VITE_ prefix */
function isOptionalVar(varName: string) {
  return optionalEnvVars.includes(varName.replace(VITE_PREFIX, ''));
}

const isDev = import.meta.env.DEV;

const runtimeConfig = window.__APP_CONFIG__ as RuntimeConfig | undefined;

function getRequiredValueOrComplain(name: string, value: unknown): string {
  if (!value || typeof value !== 'string') {
    throw new Error(`Missing required config variable: ${name}`);
  }
  return value;
}

export const config: CatsConfig = (() => {
  if (isDev) {
    // Development: use Vite env
    return {
      FHIR_URL: getFromViteEnv('VITE_FHIR_URL'),
      MSW_URL: getFromViteEnv('VITE_MSW_URL'),
      MSW_TERMINOLOGY_URL: getFromViteEnv('VITE_MSW_TERMINOLOGY_URL'),
      TERMINOLOGY_URL: getFromViteEnv('VITE_TERMINOLOGY_URL'),
      KEYCLOAK_URL: getFromViteEnv('VITE_KEYCLOAK_URL'),
      KEYCLOAK_REALM: getFromViteEnv('VITE_KEYCLOAK_REALM'),
      KEYCLOAK_CLIENT: getFromViteEnv('VITE_KEYCLOAK_CLIENT'),
      MOD_TIME_THRESHOLD: Number(getFromViteEnv('VITE_MOD_TIME_THRESHOLD')),
      CLINICALTRIALS_SYSTEM_URL: getFromViteEnv(
        'VITE_CLINICALTRIALS_SYSTEM_URL'
      ),
      MOLIT_ID_SYSTEM_URL: getFromViteEnv('VITE_MOLIT_ID_SYSTEM_URL'),
      DRKS_SYSTEM_URL: getFromViteEnv('VITE_DRKS_SYSTEM_URL'),
      TRIALS_BACKEND_URL: getFromViteEnv('VITE_TRIALS_BACKEND_URL'),
      SUPPORT_MAIL: getFromViteEnv('VITE_SUPPORT_MAIL'),
      OVERPASS_URL: getFromViteEnv('VITE_OVERPASS_URL'),
      CHANGED_WILDTYPE_GENE_SYSTEM: getFromViteEnv(
        'VITE_CHANGED_WILDTYPE_GENE_SYSTEM'
      ),
      MATOMO_ENABLED: Boolean(getFromViteEnv('VITE_MATOMO_ENABLED')),
      MATOMO_URL: getFromViteEnv('VITE_MATOMO_URL'),
      MATOMO_SITE_ID: getFromViteEnv('VITE_MATOMO_SITE_ID'),
      CATS_DOCS_PATIENTS: getFromViteEnv('VITE_CATS_DOCS_PATIENTS'),
      CATS_DOCS: getFromViteEnv('VITE_CATS_DOCS'),
      SENTRY_ACTIVE: Boolean(getFromViteEnv('VITE_SENTRY_ACTIVE')),
      SENTRY_DSN: getFromViteEnv('VITE_SENTRY_DSN'),
      SENTRY_REPLAY_ACTIVE: Boolean(
        getFromViteEnv('VITE_SENTRY_REPLAY_ACTIVE')
      ),
      SENTRY_TRACING_ACTIVE: Boolean(
        getFromViteEnv('VITE_SENTRY_TRACING_ACTIVE')
      ),
      SENTRY_ENVIRONMENT: getFromViteEnv('VITE_SENTRY_ENVIRONMENT'),
    };
  }

  // Production: require runtime injection
  if (!runtimeConfig) {
    throw new Error('Runtime config not loaded');
  }

  return {
    FHIR_URL: getFromRuntimeEnv('FHIR_URL'),
    MSW_URL: getFromRuntimeEnv('MSW_URL'),
    MSW_TERMINOLOGY_URL: getFromRuntimeEnv('MSW_TERMINOLOGY_URL'),
    TERMINOLOGY_URL: getFromRuntimeEnv('TERMINOLOGY_URL'),
    KEYCLOAK_URL: getFromRuntimeEnv('KEYCLOAK_URL'),
    KEYCLOAK_REALM: getFromRuntimeEnv('KEYCLOAK_REALM'),
    KEYCLOAK_CLIENT: getFromRuntimeEnv('KEYCLOAK_CLIENT'),
    MOD_TIME_THRESHOLD: Number(getFromRuntimeEnv('MOD_TIME_THRESHOLD')),
    CLINICALTRIALS_SYSTEM_URL: getFromRuntimeEnv('CLINICALTRIALS_SYSTEM_URL'),
    MOLIT_ID_SYSTEM_URL: getFromRuntimeEnv('MOLIT_ID_SYSTEM_URL'),
    DRKS_SYSTEM_URL: getFromRuntimeEnv('DRKS_SYSTEM_URL'),
    TRIALS_BACKEND_URL: getFromRuntimeEnv('TRIALS_BACKEND_URL'),
    SUPPORT_MAIL: getFromRuntimeEnv('SUPPORT_MAIL'),
    OVERPASS_URL: getFromRuntimeEnv('OVERPASS_URL'),
    CHANGED_WILDTYPE_GENE_SYSTEM: getFromRuntimeEnv(
      'CHANGED_WILDTYPE_GENE_SYSTEM'
    ),
    MATOMO_ENABLED: Boolean(getFromRuntimeEnv('MATOMO_ENABLED')),
    MATOMO_URL: getFromRuntimeEnv('MATOMO_URL'),
    MATOMO_SITE_ID: getFromRuntimeEnv('MATOMO_SITE_ID'),
    CATS_DOCS_PATIENTS: getFromRuntimeEnv('CATS_DOCS_PATIENTS'),
    CATS_DOCS: getFromRuntimeEnv('CATS_DOCS'),
    SENTRY_ACTIVE: Boolean(getFromRuntimeEnv('SENTRY_ACTIVE')),
    SENTRY_DSN: getFromRuntimeEnv('SENTRY_DSN'),
    SENTRY_REPLAY_ACTIVE: Boolean(getFromRuntimeEnv('SENTRY_REPLAY_ACTIVE')),
    SENTRY_TRACING_ACTIVE: Boolean(getFromRuntimeEnv('SENTRY_TRACING_ACTIVE')),
    SENTRY_ENVIRONMENT: getFromRuntimeEnv('SENTRY_ENVIRONMENT'),
  };
})();

function getFromViteEnv(name: string) {
  if (isOptionalVar(name)) {
    return import.meta.env[name];
  }
  return getRequiredValueOrComplain(name, import.meta.env[name]);
}

function getFromRuntimeEnv(name: string) {
  if (isOptionalVar(name)) {
    return runtimeConfig?.[name];
  }
  return getRequiredValueOrComplain(name, runtimeConfig?.[name]);
}

export default config;
